import {useEffect, useRef} from 'react';
import {useDispatch} from 'react-redux';

import {api} from 'nutshell-core/api';
import {Nut_Settings_User} from 'nutshell-core/settings-constants';
import type {User} from 'nutshell-core/users/users-types';

import {useGetSetting} from '../../core/use-get-setting';
import {useCoreEntityScrollCheckpoint} from '../../core-entity/hooks/use-core-entity-scroll-checkpoint';
import {CoreEntityContentContainer} from '../../core-entity/layout/core-entity-content-container';
import {CoreEntityMainColumn} from '../../core-entity/layout/core-entity-main-column';
import {CoreEntityPageContainer} from '../../core-entity/layout/core-entity-page-container';
import {LoadingPage} from '../../core-entity/layout/loading-page';
import {TAB_ACTIVITY, TAB_NOTE} from '../../core-entity/redux/consts';
import {setActionBarTab} from '../../core-entity/redux/core-entity-page-reducer';

import {PeepRedesignOnboardingModal} from '../onboarding/peep-redesign-onboarding-modal';

import {AccountContent} from './content/account-content';
import {useGetAccount} from './graphql/hooks/use-get-account';
import {useGetAccountLastContactedTime} from './graphql/hooks/use-get-account-last-contacted-time';
import {AccountHeader} from './header/account-header';
import {useAccountUpdateRefetch} from './hooks/use-account-update-refetch';
import {useUpdateAccountHandlers} from './hooks/use-update-account-handlers';
import {AccountSidebar} from './sidebar/account-sidebar';

interface Props {
    accountId: string;
    user: User;
}

export function AccountPage(props: Props) {
    const lastViewedAccountId = useRef<string | null>(null);

    const {
        hasScrolledPastCheckpoint,
        scrollContainerTop,
        scrollContainerEl,
        setScrollContainerRef,
        setActionBarRef,
    } = useCoreEntityScrollCheckpoint();

    const {account, isLoading: isAccountLoading} = useGetAccount(props.accountId);
    const {accountLastContact, isLoading: isLoadingAccountLastContact} =
        useGetAccountLastContactedTime(props.accountId);

    const {
        handleUpdateName,
        handleUpdateAvatar,
        handleUpdateAssignee,
        handleUpdateDeletedStatus,
        handleUpdateDescription,
        handleHardDelete,
        handleUpdateAccountTerritory,
        handleUpdateAccountType,
        handleUpdateAccountIndustry,
        handleUpdateAccountRevenue,
        handleUpdateAccountEmployeeCount,
        handleUpdateTags,
    } = useUpdateAccountHandlers(account);

    const {setting: isSidebarCollapsed, isFirstLoad: isSidebarCollapsedLoading} = useGetSetting({
        settingName: Nut_Settings_User.SETTING_ACCOUNT_WORKSPACE_SIDEBAR_COLLAPSED,
    });
    const {setting: accountPageActionBarTab, isFirstLoad: isAccountPageActionBarTabLoading} =
        useGetSetting({
            settingName: Nut_Settings_User.SETTING_ACCOUNT_PAGE_ACTION_BAR_TAB,
        });

    useAccountUpdateRefetch(props.accountId);

    const dispatch = useDispatch();

    const sidebarCollapsedState = Boolean(
        isSidebarCollapsed && Number(isSidebarCollapsed.value) === 1
    );

    // When the tab loads via setting, update the redux state
    useEffect(() => {
        if (
            accountPageActionBarTab &&
            (accountPageActionBarTab.value === TAB_NOTE ||
                accountPageActionBarTab.value === TAB_ACTIVITY)
        ) {
            dispatch(setActionBarTab(accountPageActionBarTab.value));
        }
    }, [accountPageActionBarTab, dispatch]);

    useEffect(() => {
        if (account && lastViewedAccountId.current !== account.id) {
            lastViewedAccountId.current = account.id;
            api.post(`views/${account.id}`, {});
        }
    }, [account]);

    if (
        !account ||
        isSidebarCollapsedLoading ||
        isAccountLoading ||
        isAccountPageActionBarTabLoading
    ) {
        return <LoadingPage />;
    }

    const hasFullHeightSidebar = NutClientConfig.hasCoreEntityFullHeightSidebarFF;

    const headerEl = (
        <AccountHeader
            account={account}
            accountLastContact={accountLastContact}
            isLoadingAccountLastContact={isLoadingAccountLastContact}
            user={props.user}
            onUpdateAccountName={handleUpdateName}
            onUpdateAvatar={handleUpdateAvatar}
            onUpdateAssignee={handleUpdateAssignee}
            onUpdateDeletedStatus={handleUpdateDeletedStatus}
            onUpdateDescription={handleUpdateDescription}
            onHardDelete={handleHardDelete}
            onUpdateAccountTerritory={handleUpdateAccountTerritory}
            onUpdateAccountType={handleUpdateAccountType}
            onUpdateAccountIndustry={handleUpdateAccountIndustry}
            onUpdateAccountRevenue={handleUpdateAccountRevenue}
            onUpdateAccountEmployeeCount={handleUpdateAccountEmployeeCount}
            onUpdateTags={handleUpdateTags}
        />
    );

    const contentEl = (
        <AccountContent
            account={account}
            scrollContainerRef={scrollContainerEl}
            user={props.user}
            hasScrolledPastCheckpoint={hasScrolledPastCheckpoint}
            getActionBarRef={setActionBarRef}
            isSidebarCollapsed={sidebarCollapsedState}
            scrollContainerTop={scrollContainerTop}
            onUpdateAssignee={handleUpdateAssignee}
            onUpdateDeletedStatus={handleUpdateDeletedStatus}
            onHardDelete={handleHardDelete}
            hasFullHeightSidebar={hasFullHeightSidebar}
        />
    );

    const sidebarEl = (
        <AccountSidebar
            account={account}
            user={props.user}
            hasScrolledPastCheckpoint={hasScrolledPastCheckpoint}
            hasFullHeightSidebar={hasFullHeightSidebar}
        />
    );

    return (
        <CoreEntityPageContainer
            title={`Company · ${account.name}`}
            hasFullHeightSidebar={hasFullHeightSidebar}
            scrollContainerRef={hasFullHeightSidebar ? undefined : setScrollContainerRef}
        >
            {hasFullHeightSidebar ? (
                <>
                    <CoreEntityMainColumn scrollContainerRef={setScrollContainerRef}>
                        {headerEl}
                        {contentEl}
                    </CoreEntityMainColumn>
                    {sidebarEl}
                </>
            ) : (
                <>
                    {headerEl}
                    <CoreEntityContentContainer>
                        {contentEl}
                        {sidebarEl}
                    </CoreEntityContentContainer>
                </>
            )}
            <PeepRedesignOnboardingModal />
        </CoreEntityPageContainer>
    );
}
